export type ConstraintPriorities = 'OPTIMIZING' | 'IMPORTANT' | 'MANDATORY';

export type ConstraintTypes =
  | 'startEnd'
  | 'minMaxHours'
  | 'maxFreePeriod'
  | 'sameEnd'
  | 'lunchBreak'
  | 'maxOnUnknownPosition'
  | 'maxBuildingChangesPerDay'
  | 'maxBuildingChangesPerWeek';

export const constraintOrder: ConstraintTypes[] = [
  'startEnd',
  'minMaxHours',
  'maxFreePeriod',
  'sameEnd',
  'lunchBreak',
  'maxOnUnknownPosition',
  'maxBuildingChangesPerDay',
  'maxBuildingChangesPerWeek',
];

export type ConstraintsSelectType = {
  label: string;
  value: ConstraintTypes;
};

export type ConstraintsFormInitialValuesType = {
  comment: string;
  startEnd?: { latestStart?: string; earliestEnd?: string };
  startEndPriority?: ConstraintPriorities;
  minMaxHours?: { min?: number; max?: number };
  minMaxHoursPriority?: ConstraintPriorities;
  maxFreePeriod?: number;
  maxFreePeriodPriority?: ConstraintPriorities;
  sameEnd?: boolean;
  sameEndPriority?: ConstraintPriorities;
  lunchBreak?: { from?: string; until?: string };
  lunchBreakPriority?: ConstraintPriorities;
  maxOnUnknownPosition?: number;
  maxOnUnknownPositionPriority?: ConstraintPriorities;
  maxBuildingChangesPerDay?: number;
  maxBuildingChangesPerDayPriority?: ConstraintPriorities;
  maxBuildingChangesPerWeek?: number;
  maxBuildingChangesPerWeekPriority?: ConstraintPriorities;
};

export type ConstraintsItemType = {
  /** Domain-specific constraint key (class / teacher / subject). The shared ConstraintsItem renderer
   *  is type-agnostic; each domain's hook produces its own keys. */
  type: string;
  text: string;
  priority: ConstraintPriorities | null;
};

export type ConditionsDuplicateTarget = { value: string; label: string };

/**
 * Conditions-tab controls that the resource-page shell owns (so the add/edit + duplicate buttons sit
 * in the shared action bar) and hands to each domain's conditions component, which renders the modals.
 */
export type ConditionsControlProps = {
  editOpen: boolean;
  setEditOpen: (open: boolean) => void;
  duplicateOpen: boolean;
  setDuplicateOpen: (open: boolean) => void;
  /** Other resources of the same type the current conditions can be copied to. */
  duplicateTargets: ConditionsDuplicateTarget[];
};

/**
 * Canonical (upper-case) constraint priority. The form now writes upper-case to match the solver wire
 * / `DependencyPriority`, but older records were persisted lower-cased ('mandatory'…). This tolerates
 * both on read; anything unrecognised → undefined.
 */
export const toConstraintPriority = (value?: string | null): ConstraintPriorities | undefined => {
  const upper = value?.toUpperCase();
  return upper === 'MANDATORY' || upper === 'IMPORTANT' || upper === 'OPTIMIZING' ? upper : undefined;
};

/** Upper-case every priority field of a loaded constraints record so old lower-case data still matches. */
export const normalizeConstraintPriorities = (
  constraints: ConstraintsFormInitialValuesType,
): ConstraintsFormInitialValuesType => ({
  ...constraints,
  startEndPriority: toConstraintPriority(constraints.startEndPriority),
  minMaxHoursPriority: toConstraintPriority(constraints.minMaxHoursPriority),
  maxFreePeriodPriority: toConstraintPriority(constraints.maxFreePeriodPriority),
  sameEndPriority: toConstraintPriority(constraints.sameEndPriority),
  lunchBreakPriority: toConstraintPriority(constraints.lunchBreakPriority),
  maxOnUnknownPositionPriority: toConstraintPriority(constraints.maxOnUnknownPositionPriority),
  maxBuildingChangesPerDayPriority: toConstraintPriority(constraints.maxBuildingChangesPerDayPriority),
  maxBuildingChangesPerWeekPriority: toConstraintPriority(constraints.maxBuildingChangesPerWeekPriority),
});
